// ─────────────────────────────────────────────────────────────
// hooks/useSolPredStore.jsx
//
// 整个应用的状态中心 (Context + Provider)。
// 所有体系选择、温度、点击选中、UI 开关都在这里。
// 子组件通过 useSolPred() 取自己关心的字段，避免一层层传 props。
//
// 阶段 2 改动指南:
//   ▸ 把 useMemo 里的 generateXxx() 换成 useQuery() 拉真接口即可
//   ▸ 其它字段一律不动
// ─────────────────────────────────────────────────────────────

import { createContext, useContext, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { SYSTEMS, SOLID_PHASES } from '../data/systems';
import {
  generateDataPoints,
  generatePredictionCurve,
  generateInvariantPoints,
  generateModelDiagnostics,
} from '../api/mockApi';

const SolPredContext = createContext(null);

export const useSolPred = () => {
  const ctx = useContext(SolPredContext);
  if (!ctx) throw new Error('useSolPred must be used inside <SolPredProvider>');
  return ctx;
};

export function SolPredProvider({ children }) {
  // ── 体系/温度/筛选 ──────────────────────────────
  const [selectedSystem,    setSelectedSystem]    = useState(SYSTEMS[0]);
  const [temperature,       setTemperature]       = useState(25);
  const [searchQuery,       setSearchQuery]       = useState('');
  const [selectedMolecules, setSelectedMolecules] = useState(new Set());

  // ── UI 开关 ────────────────────────────────────
  const [showPrediction, setShowPrediction] = useState(true);
  const [showRegions,    setShowRegions]    = useState(true);
  const [activeTab,      setActiveTab]      = useState('isothermal');
  const [selectedPhase,  setSelectedPhase]  = useState('all');
  const [autoRotate,     setAutoRotate]     = useState(true);

  // ── 交互状态 ───────────────────────────────────
  const [hoveredPoint,  setHoveredPoint]  = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [predicting,    setPredicting]    = useState(false);

  // ── 实时预测面板 ─────────────────────────────────
  const [liveW1,     setLiveW1]     = useState(0.18);
  const [liveW2,     setLiveW2]     = useState(0.06);
  const [liveResult, setLiveResult] = useState(null);

  // ── 数据表自动滚动到选中行 ─────────────────────────
  const selectedRowRef = useRef(null);
  useEffect(() => {
    if (activeTab === 'data' && selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedPoint?.id, activeTab]);

  // ── 派生数据 (阶段 2 这些 useMemo 换成 useQuery) ────
  const dataPoints = useMemo(
    () => generateDataPoints(selectedSystem.id, temperature),
    [selectedSystem.id, temperature]
  );
  const predictions = useMemo(
    () => generatePredictionCurve(selectedSystem.id, temperature),
    [selectedSystem.id, temperature]
  );
  const invariantPoints = useMemo(
    () => generateInvariantPoints(selectedSystem.id, temperature),
    [selectedSystem.id, temperature]
  );
  const phaseList = useMemo(
    () => SOLID_PHASES[selectedSystem.id] || [...new Set(dataPoints.map(p => p.solidPhase))],
    [selectedSystem.id, dataPoints]
  );
  const diag = useMemo(
    () => generateModelDiagnostics(selectedSystem.id),
    [selectedSystem.id]
  );
  const allData3D = useMemo(() => {
    const arr = [];
    const [tmin, tmax] = selectedSystem.tempRange;
    for (let t = tmin; t <= tmax; t += Math.max(5, Math.round((tmax - tmin) / 12))) {
      arr.push(...generateDataPoints(selectedSystem.id, t));
    }
    return arr;
  }, [selectedSystem.id]);

  // ── 体系筛选 ─────────────────────────────────
  const filteredSystems = useMemo(() => {
    let list = SYSTEMS;
    if (selectedMolecules.size > 0) {
      list = list.filter(s => [...selectedMolecules].every(m => s.components.includes(m)));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        s => s.components.some(c => c.toLowerCase().includes(q)) ||
             s.id.toLowerCase().includes(q)
      );
    }
    return list;
  }, [searchQuery, selectedMolecules]);

  const ALL_MOLECULES = useMemo(() => {
    const counts = {};
    SYSTEMS.forEach(s => s.components.forEach(c => { counts[c] = (counts[c] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([c]) => c);
  }, []);

  const stats = useMemo(() => {
    const total = SYSTEMS.reduce((a, s) => a + s.dataPoints, 0);
    const trained = SYSTEMS.filter(s => s.hasModel).length;
    const avgR2 = SYSTEMS.filter(s => s.hasModel).reduce((a, s) => a + s.r2, 0) / Math.max(1, trained);
    return { total, trained, avgR2: avgR2.toFixed(3), totalSystems: SYSTEMS.length };
  }, []);

  // ── 行为/回调 ─────────────────────────────────
  const toggleMolecule = useCallback(m => {
    setSelectedMolecules(prev => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m); else next.add(m);
      return next;
    });
  }, []);

  const handleTempChange = useCallback(val => {
    setTemperature(val);
    if (selectedSystem.hasModel) {
      setPredicting(true);
      setTimeout(() => setPredicting(false), 320);
    }
  }, [selectedSystem]);

  const handleSelectSystem = useCallback(sys => {
    setSelectedSystem(sys);
    setTemperature(Math.round((sys.tempRange[0] + sys.tempRange[1]) / 2 / 5) * 5);
    setSelectedPhase('all');
    setLiveResult(null);
    setHoveredPoint(null);
  }, []);

  const runLivePrediction = useCallback(() => {
    if (!selectedSystem.hasModel) return;
    setPredicting(true);
    setTimeout(() => {
      const ww = 1 - liveW1 - liveW2;
      const minDist = Math.min(
        ...predictions.map(curve => Math.min(...curve.map(p => Math.hypot(p.w1 - liveW1, p.w2 - liveW2))))
      );
      const phaseIdx = liveW1 > liveW2 * 1.5 ? 0 : liveW1 < liveW2 * 0.6 ? 1 : 2;
      const phase = phaseList[phaseIdx % phaseList.length];
      const saturated = minDist < 0.018;
      setLiveResult({
        ww: +ww.toFixed(4),
        phase,
        saturated,
        distance: +minDist.toFixed(4),
        confidence: +(0.78 + Math.random() * 0.18).toFixed(3),
      });
      setPredicting(false);
    }, 420);
  }, [liveW1, liveW2, predictions, phaseList, selectedSystem]);

  // ── Tab 元信息 ─────────────────────────────────
  const tabs = [
    { id: 'isothermal',  label: '等温相图',  icon: '△', desc: 'Isothermal' },
    { id: 'scatter3d',   label: '3D 散点',   icon: '◆', desc: '3D Scatter' },
    { id: 'surface3d',   label: '3D 相面',   icon: '◇', desc: '3D Surface' },
    { id: 'diagnostics', label: '模型诊断',  icon: '✦', desc: 'Diagnostics' },
    { id: 'data',        label: '数据矩阵',  icon: '▦', desc: 'Data Matrix' },
    { id: 'predict',     label: '实时预测',  icon: '→', desc: 'Live Predict' },
  ];

  const value = {
    // 基本
    selectedSystem, setSelectedSystem, handleSelectSystem,
    temperature, setTemperature, handleTempChange,
    searchQuery, setSearchQuery,
    selectedMolecules, setSelectedMolecules, toggleMolecule, ALL_MOLECULES,
    // 派生
    dataPoints, predictions, invariantPoints, phaseList, diag, allData3D,
    filteredSystems, stats,
    // UI
    showPrediction, setShowPrediction,
    showRegions, setShowRegions,
    activeTab, setActiveTab,
    selectedPhase, setSelectedPhase,
    autoRotate, setAutoRotate,
    tabs,
    // 交互
    hoveredPoint, setHoveredPoint,
    selectedPoint, setSelectedPoint,
    selectedRowRef,
    predicting, setPredicting,
    // 实时预测
    liveW1, setLiveW1, liveW2, setLiveW2,
    liveResult, setLiveResult, runLivePrediction,
  };

  return <SolPredContext.Provider value={value}>{children}</SolPredContext.Provider>;
}
